import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { JobForm } from '../form'
import { useCreateJob, useUpdateJob } from '../hooks'
import { jobSchema, type JobFormData } from '../schema'
import type { Job } from '@/types/entities'

interface JobFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    job: Job | null
}

export function JobFormDialog({ open, onOpenChange, job }: JobFormDialogProps) {
    const isEditing = !!job
    const createJob = useCreateJob()
    const updateJob = useUpdateJob()

    const form = useForm<JobFormData>({
        resolver: zodResolver(jobSchema) as any,
        defaultValues: {
            name: '',
            schedule: '0 0 * * *',
            policyId: 0,
            targetId: 0,
            enabled: true,
            notes: '',
        },
    })

    // Reset form when dialog opens/closes or job changes
    useEffect(() => {
        if (open) {
            if (job) {
                form.reset({
                    name: job.name,
                    schedule: job.schedule,
                    policyId: job.policyId,
                    targetId: job.targetId,
                    enabled: job.enabled,
                    notes: job.notes || '',
                })
            } else {
                form.reset({
                    name: '',
                    schedule: '0 0 * * *',
                    policyId: 0,
                    targetId: 0,
                    enabled: true,
                    notes: '',
                })
            }
        }
    }, [open, job, form])

    const onSubmit = async (data: JobFormData) => {
        try {
            if (isEditing && job) {
                await updateJob.mutateAsync({
                    id: job.id,
                    data,
                })
            } else {
                await createJob.mutateAsync(data)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    const isLoading = createJob.isPending || updateJob.isPending

    // Mock data for policies and targets - in real app this would come from API
    const policies = [
        { id: 1, name: 'Daily Backup Policy' },
        { id: 2, name: 'Weekly Archive Policy' },
        { id: 3, name: 'Monthly Retention Policy' },
    ]

    const targets = [
        { id: 1, name: 'Production Database' },
        { id: 2, name: 'File Server' },
        { id: 3, name: 'Application Data' },
    ]

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Job' : 'Create New Job'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update the job configuration below.'
                            : 'Configure a new backup job with schedule and targets.'}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit as any)} className="space-y-6">
                        <JobForm form={form as any} policies={policies} targets={targets} />

                        <div className="flex justify-end space-x-2 pt-4">
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}



